<template>
  <header ref="sectionRef" :style="bgImageUrl ? { backgroundImage: `url(${bgImageUrl})` } : {}" :class="[
    'workbook-category-intro bg-cover bg-center relative',
    isPrint ? 'wb-intro--page wb-intro--print bg-gray-400' : ''
  ]" class="bg-workbook-dark-grey bg-no-repeat">

    <!-- semi transp bg-->
    <div
      class="opacity-0 lg:opacity-100 mybg absolute bottom-0  lg:top-0 left-0 w-full lg:w-1/2 h-1/2 lg:h-full bg-[#98614E] mix-blend-multiply">

    </div>

    <UContainer class="flex items-end pb-12 md:pb-0 md:items-center lg:flex-1 pt-20 md:pt-0"
      :class="isPrint ? 'min-h-[2150px]' : 'min-h-[70vh] md:min-h-screen'">
      <div class="flex gap-4 flex-col lg:flex-row" :class="{ 'lg:flex-row-reverse': align === 'right' }">
        <div class="lg:pr-24  lg:text-inherit translate-0">
          <slot />
          <!--TwicImg class="mobileimg" :src="props.image + '?deployment=' + config.public.appVersion" ratio="1/1"
              intrinsic="1716x1038" refit="5p" mode="cover" placeholder="none" /-->
        </div>

      </div>
    </UContainer>
  </header>
  <div id="style-noherostyle"></div>
</template>

<script setup lang="ts">
import { computed, inject, onUnmounted, ref } from 'vue'


import { useRoute } from 'vue-router'
import { toTwicpicBgUrl } from '@/utils/productImages'
import { useIntroStore } from '@/store/intro'
import { useWindowSize } from '@vueuse/core'
import { loadGsap } from '@/utils/gsap'


import { useRuntimeConfig } from '#app'

const isDev = import.meta.dev

const config = useRuntimeConfig()

const introStore = useIntroStore()
const { width } = useWindowSize()

const isMobile = computed(() => width.value < 767)


const index = ref(0)
const sectionRef = ref<HTMLElement | null>(null)

const props = defineProps({
  image: {
    type: String,
    default: undefined
  },
  category: {
    type: String,
    default: undefined
  },
  pdfimage: {
    type: String,
    default: undefined
  },
  align: {
    type: String,
    default: 'left'
  }

})

const route = useRoute()
const displayedRoute = inject<{
  slug: { value: string }
  topCategory: { value: string }
} | null>('displayedRoute', null)

const isPrint = computed(() =>
  route.name?.toString().toLowerCase().includes('print') ||
  route.path.toLowerCase().includes('print')
)

const categorySlug = computed(() => {
  if (props.category) return String(props.category).toLowerCase()
  if (displayedRoute?.slug?.value) return String(displayedRoute.slug.value || '').toLowerCase()
  return String(route.params.slug || '').toLowerCase()
})
const topCategorySlug = computed(() =>
  String(
    displayedRoute?.topCategory?.value ??
    route.query.mode ??
    route.query.category ??
    ''
  ).toLowerCase()
)

const bgImageUrl = computed(() => {
  let img = props.image

  if (!img) {
    if (!categorySlug.value) return undefined
    const printSuffix = isPrint.value ? '_print' : ''
    const modePrefix = topCategorySlug.value && topCategorySlug.value !== 'all'
      ? `${topCategorySlug.value}_`
      : ''
    if (printSuffix && topCategorySlug.value === categorySlug.value) {
      // Avoid duplicate slug in print filenames when mode/category are the same (e.g., Tops)
      img = `/images/categories/${categorySlug.value}${printSuffix}.jpg`
    } else {
      img = `/images/categories/${modePrefix}${categorySlug.value}${printSuffix}.jpg`
    }
  }

  if (!img) return undefined

  const cacheBusted = `${img}?deployment=${config.public.appVersion}`
  return toTwicpicBgUrl(cacheBusted, isPrint.value)
})


let ctx: any | null = null

onMounted(async () => {
  index.value = introStore.mountedSlides.length

  if (sectionRef.value) {
    introStore.registerSlide(sectionRef.value)
  }

  if (isPrint.value) {
    return
  }
  if (process.client) {
    const { gsap } = await loadGsap()


    ctx = gsap.context(() => {
      const h1 = sectionRef.value?.querySelector('h1')

      const pElements = sectionRef.value?.querySelectorAll('p') || []
      const bg = sectionRef.value?.querySelector('.mybg');

      if (h1 && pElements && bg) {
        let tl = gsap.timeline({
          scrollTrigger: {
            trigger: sectionRef.value,
            pin: index.value === 99,
            markers: isDev,
            start: isMobile.value ? "bottom 60%" : "bottom 98%",
            end: "bottom 20%",
            scrub: 2

          }
        })




        tl.fromTo(h1, { x: 0 }, { x: -100, duration: 10 }, "+=0")
        tl.fromTo(h1, { opacity: 1 }, { opacity: 0, duration: 2 }, "-=6")


        tl.fromTo(pElements, { y: 0 }, { y: 100, duration: 10 }, "-=7")
        tl.fromTo(pElements, { opacity: 1 }, { opacity: 0, duration: 4, stagger: -1 }, "-=6")


        tl.fromTo(bg, { x: 0 }, { x: '-100%', duration: 15 })
        tl.fromTo(bg, { opacity: 1 }, { opacity: 0, duration: 10 }, "-=5")

      } else {
        console.log('Missing elements in slide at index', index.value)
      }


    }, sectionRef.value || undefined)
  }
})

onUnmounted(() => {
  ctx?.revert()
  ctx = null
})

</script>

<style scoped>
@reference "../../assets/css/main.css";

header :deep(h1) {
  @apply text-white;
}

header :deep(h2) {
  @apply text-white;
}

header :deep(p) {
  @apply text-white max-w-lg mt-4 text-2xl;
}

header.wb-intro--animated :deep(h1),
header.wb-intro--animated :deep(p) {
  opacity: 0;
}

/* Print-specific styling helpers */
@media print {
  header :deep(h1) {
    @apply opacity-100;
  }

  .wb-intro--page {
    page-break-after: always;
    break-after: page;
  }

  .workbook-category-intro img {
    break-inside: avoid;
    page-break-inside: avoid;
  }
}
</style>
